import { IMAGES, img } from "../config";

export const DESTINATIONS = [
  {
    slug: "dubai",
    name: "Dubai",
    country: "United Arab Emirates",
    image: img(IMAGES.dubai, 1400),
    intro:
      "A global crossroads for business and leisure, Dubai is one of the world's busiest private aviation markets — and our home region. We arrange arrivals and departures through dedicated private terminals, away from the main airport flow.",
    airports: [
      { name: "Dubai International (DXB)", note: "Dedicated executive terminals (FBOs) on the south side" },
      { name: "Al Maktoum International (DWC)", note: "Dubai South — preferred by many private operators" },
    ],
    why: [
      "Discreet arrivals through executive terminals with private immigration",
      "Positioning hub for onward travel across the Gulf, Indian Ocean and Europe",
      "Helicopter transfers available to Palm Jumeirah, Abu Dhabi and resort helipads",
    ],
    faqs: [
      {
        q: "Which airport should I use for private flights in Dubai?",
        a: "Both DXB and Al Maktoum (DWC) handle private aviation through dedicated executive terminals. We recommend the airport and terminal based on your ground destination and the operator's positioning.",
      },
      {
        q: "Can you arrange helicopter transfers within the UAE?",
        a: "Yes. Through our operator network we can arrange helicopter transfers between airports, city helipads and resort properties, subject to landing permissions.",
      },
      {
        q: "How much notice is needed to charter from Dubai?",
        a: "Dubai has a deep pool of based aircraft, so short-notice requests can often be accommodated — always subject to aircraft and crew availability at the time of enquiry.",
      },
    ],
  },
  {
    slug: "delhi",
    name: "Delhi",
    country: "India",
    image: img(IMAGES.delhi, 1400),
    intro:
      "India's capital is a primary gateway for government, corporate and leisure charter. Private flights operate through dedicated general aviation facilities at Indira Gandhi International Airport.",
    airports: [
      { name: "Indira Gandhi International (DEL)", note: "General aviation terminal for private movements" },
    ],
    why: [
      "Direct access to government, diplomatic and corporate districts",
      "Gateway for multi-stop itineraries across North India and the Himalaya foothills",
      "Connections onward to the Gulf, Southeast Asia and Europe",
    ],
    faqs: [
      {
        q: "Which terminal do private jets use in Delhi?",
        a: "Private and business aviation movements use the general aviation terminal at Delhi Airport, with dedicated handling, customs and immigration.",
      },
      {
        q: "Can you arrange multi-city charters within India?",
        a: "Yes. We regularly arrange multi-stop Indian itineraries — for example Delhi, Jaipur, Udaipur and Mumbai — through Indian-registered and international operators, subject to permits.",
      },
      {
        q: "Are there restrictions on international private flights into India?",
        a: "India requires landing permits for foreign-registered aircraft, arranged by the operator with lead time. We manage this coordination as part of every enquiry.",
      },
    ],
  },
  {
    slug: "mumbai",
    name: "Mumbai",
    country: "India",
    image: img(IMAGES.mumbai, 1400),
    intro:
      "India's financial capital generates year-round demand for business charter — banking roadshows, film production and leisure travel alike. Private movements operate from dedicated general aviation facilities at CSIA.",
    airports: [
      { name: "Chhatrapati Shivaji Maharaj International (BOM)", note: "General aviation apron and terminal" },
      { name: "Juhu Aerodrome (VAJJ)", note: "Helicopter operations within the city" },
    ],
    why: [
      "Fast access to BKC, Nariman Point and South Mumbai business districts",
      "Helicopter links from Juhu for city transfers and coastal resorts",
      "Strong connectivity to the Gulf, Maldives and Southeast Asia",
    ],
    faqs: [
      {
        q: "Can helicopters operate within Mumbai?",
        a: "Yes — helicopter transfers operate from Juhu Aerodrome and approved helipads, subject to availability and local permissions.",
      },
      {
        q: "How early should I book a charter from Mumbai?",
        a: "As early as practical — Mumbai slots and parking are constrained at peak times. Short-notice requests are handled case by case, subject to availability.",
      },
      {
        q: "Can I charter directly from Mumbai to the Maldives?",
        a: "Yes, this is a frequently requested leisure route. We source suitable midsize and above aircraft through our operator network for the overwater sector.",
      },
    ],
  },
  {
    slug: "maldives",
    name: "Maldives",
    country: "Maldives",
    image: img(IMAGES.maldives, 1400),
    intro:
      "The Maldives is one of the most requested leisure destinations we serve. International private flights arrive at Velana International Airport, with seaplane, speedboat or helicopter connections onward to resort atolls.",
    airports: [
      { name: "Velana International (MLE)", note: "Main international gateway, Malé" },
      { name: "Resort seaplane & helipad network", note: "Arranged in coordination with your resort" },
    ],
    why: [
      "Direct private access from the Gulf, India, Southeast Asia and Europe",
      "Seamless coordination with resort seaplane and transfer schedules",
      "Discreet handling for high-profile leisure travel",
    ],
    faqs: [
      {
        q: "How do I reach my resort after landing at Malé?",
        a: "Most resorts are reached by seaplane (daylight hours), speedboat or domestic flight. We coordinate your charter arrival timing with the resort's transfer schedule wherever possible.",
      },
      {
        q: "Can I fly directly from Dubai or India to the Maldives?",
        a: "Yes — these are core leisure routes. Depending on departure point, we typically source super midsize or heavy jets for the sector.",
      },
      {
        q: "Do aircraft park overnight in the Maldives?",
        a: "Parking at Velana is limited and often expensive during peak season. Many clients release the aircraft and rebook for the return — we advise on the most sensible arrangement per trip.",
      },
    ],
  },
  {
    slug: "london",
    name: "London",
    country: "United Kingdom",
    image: img(IMAGES.london, 1400),
    intro:
      "London is served by a ring of dedicated business aviation airports. We select the airfield that minimises your ground transfer — Farnborough, Luton and Biggin Hill among the most used.",
    airports: [
      { name: "Farnborough (FAB)", note: "Purpose-built business aviation airport" },
      { name: "London Luton (LTN)", note: "Multiple FBOs, 24-hour operations" },
      { name: "Biggin Hill (BQH)", note: "Close to the City and Canary Wharf" },
    ],
    why: [
      "Choice of dedicated private airports around the capital",
      "Direct long-haul connectivity to the Gulf, US and Asia",
      "Experienced handling for corporate roadshows and events",
    ],
    faqs: [
      {
        q: "Which London airport is best for private jets?",
        a: "It depends on your final destination in London and slot availability. Farnborough, Luton and Biggin Hill are the most frequently used; we recommend per itinerary.",
      },
      {
        q: "Can you arrange chauffeured transfers from the airfield?",
        a: "Yes — ground transport can be coordinated through the handling agent or your own arrangements, whichever you prefer.",
      },
      {
        q: "Are there night restrictions at London's private airports?",
        a: "Some airfields have night curfews or quota systems. We confirm operating hours for your schedule before quoting.",
      },
    ],
  },
  {
    slug: "singapore",
    name: "Singapore",
    country: "Singapore",
    image: img(IMAGES.singapore, 1400),
    intro:
      "Singapore is Southeast Asia's private aviation hub. Business jets typically operate through Seletar Airport's dedicated executive facilities, with Changi available for specific operations.",
    airports: [
      { name: "Seletar Airport (XSP)", note: "Primary business aviation gateway" },
      { name: "Singapore Changi (SIN)", note: "Used for certain operations and connections" },
    ],
    why: [
      "Regional hub for Southeast Asia corporate and leisure itineraries",
      "Efficient, discreet executive terminal experience",
      "Onward access to Indonesia, Thailand, Malaysia and beyond",
    ],
    faqs: [
      {
        q: "Which airport do private jets use in Singapore?",
        a: "Most business aviation uses Seletar (XSP), which offers dedicated executive handling. Changi is used for certain operations — we advise per trip.",
      },
      {
        q: "Can I combine Singapore with island destinations nearby?",
        a: "Yes. Bintan, Langkawi, Phuket and Bali are common pairings, arranged as multi-stop charters through regional operators.",
      },
      {
        q: "How far is Seletar from the city centre?",
        a: "Seletar is roughly 25–30 minutes by road from the central business district, depending on traffic.",
      },
    ],
  },
];
